import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { addProductToCart } from "../../../hooks/useSupabase";
import { ProductCard } from "./Cards/ProductCard";
import { ProductListCard } from "./Cards/ProductListCard";
import type { Product, ProductGridProps } from "./Types";
import { useWishlistContext } from "../../../hooks/useWishlistContext";

const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  viewMode,
  loading,
  isShowWishlist = false,
  is_HScroll = false,
  filtering = false,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adding, setAdding] = React.useState<{ [id: number]: boolean }>({});
  const { wishlistedIds } = useWishlistContext();

  const handleAddToCart = async (productId: number) => {
    if (!user) {
      navigate("/registration");
      return;
    }

    setAdding((prev) => ({ ...prev, [productId]: true }));
    try {
      await addProductToCart({ productId, quantity: 1 });
    } finally {
      setAdding((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const renderCard = (product: Product) => {
    const isWishlisted = wishlistedIds?.includes(product.id) || false;
    const cardProps = {
      product,

      adding: !!adding[product.id],
      onAddToCart: () => handleAddToCart(product.id),
    };

    return viewMode === "list" ? (
      <ProductListCard {...cardProps} />
    ) : (
      <ProductCard
        {...cardProps}
        isWishlisted={isWishlisted}
        isShowWishlist={isShowWishlist}
      />
    );
  };

  if (loading && products.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <div className="text-lg text-gray-600">Loading products...</div>
        </div>
      </div>
    );
  }

  if (!loading && products.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 text-lg">No products found</div>
        <div className="text-gray-400 text-sm mt-2">
          Try adjusting your filters
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {filtering && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="flex items-center space-x-3 bg-white px-4 py-2 rounded-lg shadow-md">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            <span className="text-gray-600 font-medium">
              Updating results...
            </span>
          </div>
        </div>
      )}

      {is_HScroll ? (
        <>
          {/* Horizontal Scroll on Mobile */}
          <div className="block md:hidden">
            <div
              className={`flex overflow-x-auto gap-4 pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide ${
                filtering ? "opacity-75" : ""
              }`}
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {products.map((product) => (
                <div key={product.id} className="w-70 shrink-0 snap-start">
                  {renderCard(product)}
                </div>
              ))}
            </div>
          </div>

          {/* Grid on Desktop */}
          <div
            className={`hidden md:grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${
              filtering ? "opacity-75" : ""
            }`}
          >
            {products.map((product) => (
              <div key={product.id}>{renderCard(product)}</div>
            ))}
          </div>
        </>
      ) : (
        <div
          className={`${
            viewMode === "list"
              ? "flex flex-col gap-4 max-w-5xl mx-auto"
              : "grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          } ${filtering ? "opacity-75" : ""}`}
        >
          {products.map((product) => (
            <div key={product.id}>{renderCard(product)}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
